import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useSharedState } from "state";

export interface PaginationType {
  total: number;
  updateInfo: (offset: number) => Promise<void>;
}

export default function Pagination({ total, updateInfo }: PaginationType) {
  const { offset, firstEntry, updateFirstEntry } = useSharedState();

  const [lastEntry, setLastEntry] = useState<number>(0);
  const [pagesCount, setPagesCount] = useState<number[]>([]);

  const pageSize = 10;
  const currentPage = Math.floor(offset / pageSize) + 1;

  const visiblePages = pagesCount.filter(
    (p) => p === 1 || p === pagesCount.length || (p >= currentPage - 1 && p <= currentPage + 1)
  );

  const updatePagination = async (newOffset?: number) => {
    newOffset !== undefined && updateInfo(newOffset);

    let newFirstEntry = offset + 1;
    updateFirstEntry(newFirstEntry);
    setLastEntry(Math.min(offset + pageSize, total));
    setPagesCount(Array.from({ length: Math.ceil(total / pageSize) }, (_, i) => i + 1));
  };

  const prevPage = () => {
    const newOffset = offset - pageSize;
    newOffset >= 0 && updatePagination(newOffset);
  };

  const nextPage = () => {
    const newOffset = offset + pageSize;
    newOffset < total && updatePagination(newOffset);
  };

  const goToPage = (page: number) => {
    const newOffset = (page - 1) * pageSize;
    updatePagination(newOffset);
  };

  useEffect(() => {
    updatePagination();
  }, [updateInfo]);

  return (
    <>
      <div className="card-footer d-flex align-items-center">
        <p className="m-0 text-muted">
          Показаны {firstEntry}-{lastEntry} из {total} записей
        </p>
        <ul className="pagination m-0 ms-auto">
          <li className={`page-item ${currentPage === 1 ? "disabled" : "cursor-pointer"}`} onClick={prevPage}>
            <div className="page-link">
              <IconChevronLeft className="icon" />
            </div>
          </li>
          {visiblePages.map((page, index) => {
            if (index > 0 && visiblePages[index - 1] !== page - 1) {
              return (
                <li className="d-flex page-item" key={index}>
                  <span className="page-link pe-none">...</span>
                  <div onClick={() => goToPage(page)} className="page-link cursor-pointer">
                    {page}
                  </div>
                </li>
              );
            }

            return (
              <li
                className={`page-item ${page === currentPage ? "active" : "cursor-pointer"}`}
                key={index}
                onClick={() => page !== currentPage && goToPage(page)}
              >
                <div className="page-link">{page}</div>
              </li>
            );
          })}
          <li
            className={`page-item ${currentPage === pagesCount.length ? "disabled" : "cursor-pointer"}`}
            onClick={nextPage}
          >
            <div className="page-link">
              <IconChevronRight className="icon" />
            </div>
          </li>
        </ul>
      </div>
    </>
  );
}
